import React from 'react'
import { formatPriceNumber } from '@/utils/priceFormatting'
import { IAddonItem } from '@/components/Addon'

interface ProductModalsProps {
    showConfiguratorModal: boolean
    showMoreColoursModal: boolean
    moreColoursData: { groupName: string; items: IAddonItem[] } | null
    selectedColorWay: string | null
    navHeight: number
    handleConfiguratorChoice: (openConfigurator: boolean) => void
    setShowMoreColoursModal: (show: boolean) => void
    setSelectedColorWay: (id: string) => void
}

const ProductModals: React.FC<ProductModalsProps> = ({
    showConfiguratorModal,
    showMoreColoursModal,
    moreColoursData,
    selectedColorWay,
    navHeight,
    handleConfiguratorChoice,
    setShowMoreColoursModal,
    setSelectedColorWay
}) => {
    return (
        <>
            {/* Configurator Modal */}
            {showConfiguratorModal && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex items-center justify-center p-4"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        height: '100vh',
                        width: '100vw',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <div
                        className="bg-white rounded-2xl p-6 max-w-md w-full shadow-lg"
                        style={{
                            transform: 'translateY(calc(-50vh + 100% + ' + navHeight + 'px))',
                            margin: 'auto'
                        }}
                    >
                        <h2 className="text-2xl font-headline mb-4">Ready to customize?</h2>
                        <p className="text-gray-600 mb-6">
                            Would you like to start customizing your headphones now, or continue reading about the product?
                        </p>
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => handleConfiguratorChoice(true)}
                                className="w-full bg-orange text-white py-3 px-6 rounded-xl font-headline text-lg
                                    transition-all duration-200 ease-kibu hover:brightness-105 hover:scale-[1.02]"
                            >
                                Start Customizing
                            </button>
                            <button
                                onClick={() => handleConfiguratorChoice(false)}
                                className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-xl font-headline text-lg
                                    transition-all duration-200 ease-kibu hover:bg-gray-200"
                            >
                                Continue Reading
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* More Colours Modal */}
            {showMoreColoursModal && moreColoursData && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex items-center justify-center p-4"
                    onClick={() => setShowMoreColoursModal(false)}
                >
                    <div
                        className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-headline">{moreColoursData.groupName} Collection</h2>
                            <button
                                onClick={() => setShowMoreColoursModal(false)}
                                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        <div className="space-y-8">
                            {(() => {
                                // Group items by their group property
                                const groupedModalItems = moreColoursData.items.reduce((acc, item) => {
                                    const groupName = item.group || 'Other';
                                    if (!acc[groupName]) {
                                        acc[groupName] = [];
                                    }
                                    acc[groupName].push(item);
                                    return acc;
                                }, {} as Record<string, IAddonItem[]>);

                                return Object.entries(groupedModalItems)
                                    .sort(([a], [b]) => {
                                        // Sort groups: Custom first, then alphabetical
                                        if (a === 'Custom') return -1;
                                        if (b === 'Custom') return 1;
                                        return a.localeCompare(b);
                                    })
                                    .map(([groupName, groupItems]) => (
                                        <div key={groupName} className="group-section">
                                            <h3 className="text-lg font-headline mb-4 text-gray-800 border-b border-gray-200 pb-2">
                                                {groupName} {groupName === 'Custom' ? 'Colours' : 'Collection'}
                                            </h3>
                                            <div className="grid grid-cols-3 md:grid-cols-5 gap-4 mb-2">
                                                {groupItems.map((item) => (
                                                    <div key={item.id} className="flex flex-col gap-2">
                                                        <button
                                                            onClick={() => {
                                                                if (item.id) {
                                                                    setSelectedColorWay(item.id);
                                                                    setShowMoreColoursModal(false);
                                                                }
                                                            }}
                                                            className={`w-full aspect-square transition-all ease-in-out border border-[3px] border-background rounded-xl 
                                                                overflow-hidden outline outline-[3px] md:hover:outline-orange hover:duration-75 md:hover:scale-105 bg-white relative
                                                                drop-shadow-addons supports-[hover:hover]:hover:outline-orange supports-[hover:hover]:hover:duration-75 supports-[hover:hover]:hover:scale-105 cursor-pointer
                                                                ${item.id === selectedColorWay
                                                                    ? 'outline-orange duration-100'
                                                                    : 'outline-transparent duration-300'
                                                                }`}
                                                        >
                                                            <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
                                                        </button>
                                                        <div className="text-center">
                                                            <h4 className="text-sm font-headline m-0">{item.title}</h4>
                                                            {item.price !== 0 && (
                                                                <h5 className={`${item.price < 0 ? 'text-gray-400' : 'text-orange'} text-sm m-0`}>
                                                                    {item.price < 0 ? '-' : '+'}{item.currency}{formatPriceNumber(Math.abs(item.price))}
                                                                </h5>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ));
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ProductModals
